import { PropTypes } from 'prop-types'
import { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Skeleton } from '@mui/material';
import { useNotificationContext } from 'contexts/NotificationContext';
import Drawer from 'features/IRWorkstation/components/Common/Drawer/Drawer';
import SingleSelect from 'components/common/SingleSelect';
import InfoIcon from '@mui/icons-material/Info';
import salesforceEntitiesService from 'services/salesforceEntitiesService';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

const EntityField = ({ label, children, value}) => (
	<Box sx={{ mb: 2 }}>
		<Typography
			sx={{
				fontSize: '11px',
				fontWeight: 600,
				textTransform: 'uppercase',
				color: 'text.tertiary',
				mb: '4px',
			}}
		>
			{label}
		</Typography>

		{children ?? (
			<Typography
				sx={{
					fontSize: '13px',
					fontWeight: 500,
					color: 'text.primary',
				}}
			>
				{value || '—'}
			</Typography>
		)}
	</Box>
);

EntityField.propTypes = {
	label: PropTypes.string.isRequired,
	children: PropTypes.node,
	value: PropTypes.node,
};

const EntityDrawer = ({ open, entity, tenant, onClose, onSaved }) => {
  const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });
  const { showNotification } = useNotificationContext();
  const [saving, setSaving] = useState(false);

  // Editable Field State
  const [entityName, setEntityName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [entityStatus, setEntityStatus] = useState('');
  const [targetSize, setTargetSize] = useState('');

  // Picklist options
  const [picklistValues, setPicklistValues] = useState(null);
  const [picklistLoading, setPicklistLoading] = useState(true);
  const picklistFetched = useRef(false);
  const drawerLoading = picklistLoading || !entity;

  const initializeFormState = (data) => {
    if (!data) return;
    setEntityName(data.name ?? ''); 
    setEntityType(data.entity_type ?? '');
    setEntityStatus(data.status ?? '');
    setTargetSize(data.target_size ?? '');
  }

  useEffect(() => {
    initializeFormState(entity);
  }, [entity])

  // Fetch picklist values once when opening the drawer
	useEffect(() => {
    if(!open || picklistFetched.current) return;

    const fetchPicklists = async () => {
      setPicklistLoading(true);
      try {
        const data = await salesforceEntitiesService.getPicklistValues();
        setPicklistValues(data);
        picklistFetched.current = true;
      } catch (err) {
        console.error('Failed to load picklist values:', err);
      } finally {
        setPicklistLoading(false);
      }
    }

    fetchPicklists();
  }, [open])

  useEffect(() => {
    trackPageLoad(PageLoadsTargets.EntityDrawer);
  }, [])

  // Build a payload of only changed fields
  const buildPayload = () => {
    if (!entity) return {};

    const payload = {};

    if (entityName !== (entity.name ?? '')) {
      payload.name = entityName;
    }

    if (entityType !== (entity.entity_type ?? '')) {
      payload.entity_type = entityType;
    }

    if (entityStatus !== (entity.status ?? '')) {
      payload.status = entityStatus;
    }

    const targetSizeNum = targetSize !== ''? Number(targetSize) : null;
    const origTargetSize = entity.target_size !== '' && entity.target_size != null ? Number(entity.target_size) : null;
    if (targetSizeNum !== origTargetSize) {
      payload.target_size = targetSizeNum;
    }

    return payload;
  }

  const handleCancel = () => {
    if (saving) return;
    handleButtonClickTrackEvent('cancel');
    initializeFormState(entity);
    onClose();
  }
  
  const handleSave = async () => {
    if (!entity) return;
    handleButtonClickTrackEvent('save');

   if (drawerLoading || saving) return;
   const payload = buildPayload();
   if (Object.keys(payload).length === 0) {
    showNotification('No changes to save', 'info');
    return;
   }
   
    setSaving(true);
    try {
    await salesforceEntitiesService.updateEntity(
      entity.id, 
      payload
    );

    await onSaved?.();
    showNotification('Entity record updated', 'success');
    onClose();
    } catch (err) {
			showNotification(
				err?.message || 'Failed to update entity',
				'error'
			);
    } finally {
      setSaving(false);
    }
  };

  const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			cancel: ButtonClickTargets.EntityDrawerCancel,
			save: ButtonClickTargets.EntityDrawerSave,
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

  
  const defaultActionButtons = [
		{
			key: 'entity-cancel-action-button',
			label: 'Cancel',
			onClick: handleCancel,
			disabled: saving,
			variant: 'secondary',
		},
		{
			key: 'entity-save-action-button',
			label: 'Save Changes',
			onClick: handleSave,
			loading: saving,
			disabled: drawerLoading,
			variant: 'primary',
		},
	];
  
  return (
    <Drawer
      open={open}
      onClose={handleCancel}
      title={'Edit Entity'}
      subtitle={entity?.name ? entity.name : ''}
      actionButtons={defaultActionButtons}
      >

      {drawerLoading ? (
        <Box
          data-testid='entity-drawer-skeleton'
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            padding: '24px',
          }}
        >
          {[...Array(4)].map((_, i) => (
            <Box key={i}>
              <Skeleton variant='text' width={80} height={16} />
              <Skeleton variant='rounded' height={40} />
            </Box>
          ))}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mx: 5,
              mt: 3,
              mb: 0,
              px: 2,
              py: 1.5,
              borderRadius: 1,
              backgroundColor: 'status.blue.bg',
              border: '1px solid',
              borderColor: 'status.blue.border',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon fontSize='small' sx={{ color: 'status.blue.icon' }} />
              <Typography sx={{ fontSize: '12px', color: 'status.blue.text' }}>
                Changes made within this modal will flow back into the Altvia CRM.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                mb: 3,
              }}
            >
              <EntityField label='Entity Name'>
                <TextField
                  size='small'
                  fullWidth
                  value={entityName}
                  onChange={(e) => setEntityName(e.target.value)}
                  disabled={saving}
                />
              </EntityField>

              <EntityField label='Entity Type'>
                <SingleSelect
                  value={entityType}
                  options={picklistValues?.entity_type || []}
                  loading={picklistLoading}
                  disabled={saving}
                  placeholder='Select an entity type'
                  onChange={(val) => setEntityType(val)}
                />
              </EntityField>

              <EntityField label='Entity Status'>
                <SingleSelect
                  value={entityStatus}
                  options={picklistValues?.status || []}
                  loading={picklistLoading}
                  disabled={saving}
                  placeholder='Select an entity status'
                  onChange={(val) => setEntityStatus(val)}
                />
              </EntityField>

              <EntityField label='Target Size'>
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  value={targetSize}
                  onChange={(e) => setTargetSize(e.target.value)}
                  disabled={saving}
                />
              </EntityField>
            </Box>
          </Box>
        </>
      )}
    </Drawer>
  )
}

EntityDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  entity: PropTypes.object,
  tenant: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func
}

export default EntityDrawer;